import { useRef, useState, type FormEvent } from 'react'
import { TemplatePage } from './TemplateChrome'
import type { TemplateProps } from './types'

const stepNames = ['Eligibility', 'Your details', 'Review'] as const
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

type Answers = { renewing: string; name: string; email: string; month: string; day: string; year: string }

/**
 * A form split into short steps: one topic per page, a step indicator, errors
 * named at the top and next to each field, and a review before submitting.
 */
export default function MultiStepForm({ mission, link, credit }: TemplateProps) {
  const [step, setStep] = useState(0)
  const [done, setDone] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof Answers, string>>>({})
  const [answers, setAnswers] = useState<Answers>({ renewing: '', name: '', email: '', month: '', day: '', year: '' })
  const heading = useRef<HTMLHeadingElement>(null)

  const set = (key: keyof Answers) => (value: string) => setAnswers((current) => ({ ...current, [key]: value }))

  const validate = () => {
    const found: Partial<Record<keyof Answers, string>> = {}
    if (step === 0 && !answers.renewing) found.renewing = 'Choose whether you are renewing a permit.'
    if (step === 1) {
      if (!answers.name.trim()) found.name = 'Enter your full name.'
      if (!/^\S+@\S+\.\S+$/.test(answers.email)) found.email = 'Enter an email address, like name@example.com.'
      if (!answers.month || !/^\d{1,2}$/.test(answers.day) || !/^\d{4}$/.test(answers.year)) found.day = 'Enter your date of birth.'
    }
    return found
  }

  const move = (next: number) => {
    setStep(next)
    setErrors({})
    // The new step's heading takes focus, so screen readers hear where they are.
    window.requestAnimationFrame(() => heading.current?.focus())
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const found = validate()
    setErrors(found)
    if (Object.keys(found).length) {
      window.requestAnimationFrame(() => document.getElementById('form-errors')?.focus())
      return
    }
    if (step < stepNames.length - 1) move(step + 1)
    else {
      setDone(true)
      window.requestAnimationFrame(() => heading.current?.focus())
    }
  }

  const describe = (key: keyof Answers, hint?: string) =>
    [hint, errors[key] ? `${key}-error` : ''].filter(Boolean).join(' ') || undefined

  return (
    <TemplatePage mission={mission} link={link} credit={credit} current="Services">
      <div className="mds-container mds-page-narrow mds-stack">
        <nav className="mds-breadcrumb" aria-label="Breadcrumb">
          <ol>
            <li>
              <a href={link('agency-home')}>Home</a>
            </li>
            <li>
              <a href={link('service-landing')}>Renew a permit</a>
            </li>
            <li aria-current="page">Application</li>
          </ol>
        </nav>
        {/* Every step is in the page with the others hidden, so the static HTML
            export can step through them with its small script, as React does here. */}
        <div className="mds-stack" data-multistep="">
          <div className="mds-steps" hidden={done} data-steps-indicator="">
            <ol className="mds-steps__list">
              {stepNames.map((name, index) => (
                <li
                  key={name}
                  className={`mds-steps__segment${index < step ? ' is-complete' : index === step ? ' is-current' : ''}`}
                  aria-current={index === step ? 'step' : undefined}
                >
                  {name}
                </li>
              ))}
            </ol>
            <p className="mds-steps__heading">
              Step <span data-step-number="">{step + 1}</span> of {stepNames.length}
            </p>
          </div>
          <h1 className="mds-h1" ref={heading} tabIndex={-1} data-step-heading="">
            {done ? 'Application received' : stepNames[step]}
          </h1>
          <div className="mds-alert mds-alert--success" role="status" hidden={!done} data-done="">
            <div>
              <p className="mds-alert__title">Your confirmation number is MDS-2026-0418</p>
              <p>
                We sent a copy to <span data-review="email">{answers.email}</span>. Expect a decision within ten business days.
              </p>
            </div>
          </div>
          {Object.keys(errors).length > 0 && (
            <div className="mds-alert mds-alert--error" id="form-errors" tabIndex={-1} role="alert">
              <div>
                <p className="mds-alert__title">Fix {Object.keys(errors).length === 1 ? 'this problem' : 'these problems'} to continue</p>
                <ul>
                  {Object.entries(errors).map(([key, message]) => (
                    <li key={key}>
                      <a href={`#field-${key}`}>{message}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <form className="mds-stack" noValidate onSubmit={submit} hidden={done} data-steps-form="" data-step-names={stepNames.join('|')}>
            <div className="mds-stack" data-step="0" hidden={step !== 0}>
              <fieldset className="mds-stack" aria-describedby={describe('renewing')}>
                <legend className="mds-label">Are you renewing a permit you already have?</legend>
                {errors.renewing && (
                  <span className="mds-error-text" id="renewing-error">
                    {errors.renewing}
                  </span>
                )}
                {[
                  ['yes', 'Yes, I am renewing'],
                  ['no', 'No, this is my first permit'],
                ].map(([value, label], index) => (
                  <label key={value} className="mds-choice">
                    <input
                      type="radio"
                      name="renewing"
                      value={value}
                      required
                      id={index === 0 ? 'field-renewing' : undefined}
                      checked={answers.renewing === value}
                      onChange={() => set('renewing')(value)}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>
            </div>
            <div className="mds-stack" data-step="1" hidden={step !== 1}>
              <div className="mds-field">
                <label className="mds-label" htmlFor="field-name">
                  Full name
                </label>
                {errors.name && (
                  <span className="mds-error-text" id="name-error">
                    {errors.name}
                  </span>
                )}
                <input
                  id="field-name"
                  name="name"
                  className="mds-input"
                  autoComplete="name"
                  required
                  value={answers.name}
                  aria-invalid={!!errors.name}
                  aria-describedby={describe('name')}
                  onChange={(event) => set('name')(event.target.value)}
                />
              </div>
              <div className="mds-field">
                <label className="mds-label" htmlFor="field-email">
                  Email
                </label>
                <span className="mds-hint" id="email-hint">
                  We send your confirmation here.
                </span>
                {errors.email && (
                  <span className="mds-error-text" id="email-error">
                    {errors.email}
                  </span>
                )}
                <input
                  id="field-email"
                  name="email"
                  className="mds-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={answers.email}
                  aria-invalid={!!errors.email}
                  aria-describedby={describe('email', 'email-hint')}
                  onChange={(event) => set('email')(event.target.value)}
                />
              </div>
              <fieldset className="mds-memorable-date" aria-describedby={describe('day')}>
                <legend className="mds-label">Date of birth</legend>
                {errors.day && (
                  <span className="mds-error-text" id="day-error">
                    {errors.day}
                  </span>
                )}
                <div className="mds-field mds-memorable-date__month">
                  <label className="mds-label" htmlFor="field-month">
                    Month
                  </label>
                  <select
                    className="mds-select"
                    id="field-month"
                    name="month"
                    autoComplete="bday-month"
                    required
                    value={answers.month}
                    onChange={(event) => set('month')(event.target.value)}
                  >
                    <option value="">Select</option>
                    {months.map((month, index) => (
                      <option key={month} value={String(index + 1)}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mds-field mds-memorable-date__day">
                  <label className="mds-label" htmlFor="field-day">
                    Day
                  </label>
                  <input
                    className="mds-input"
                    id="field-day"
                    name="day"
                    inputMode="numeric"
                    autoComplete="bday-day"
                    required
                    pattern="[0-9]{1,2}"
                    value={answers.day}
                    onChange={(event) => set('day')(event.target.value)}
                  />
                </div>
                <div className="mds-field mds-memorable-date__year">
                  <label className="mds-label" htmlFor="field-year">
                    Year
                  </label>
                  <input
                    className="mds-input"
                    id="field-year"
                    name="year"
                    inputMode="numeric"
                    autoComplete="bday-year"
                    required
                    pattern="[0-9]{4}"
                    value={answers.year}
                    onChange={(event) => set('year')(event.target.value)}
                  />
                </div>
              </fieldset>
            </div>
            <div className="mds-stack" data-step="2" hidden={step !== 2}>
              <dl className="mds-page-review">
                <div>
                  <dt>Renewing</dt>
                  <dd data-review="renewing">{answers.renewing === 'yes' ? 'Yes' : answers.renewing === 'no' ? 'No, first permit' : ''}</dd>
                </div>
                <div>
                  <dt>Full name</dt>
                  <dd data-review="name">{answers.name}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd data-review="email">{answers.email}</dd>
                </div>
                <div>
                  <dt>Date of birth</dt>
                  <dd data-review="birth">{answers.month ? `${months[Number(answers.month) - 1]} ${answers.day}, ${answers.year}` : ''}</dd>
                </div>
              </dl>
            </div>
            <div className="mds-cluster">
              <button type="button" className="mds-button mds-button--secondary" hidden={step === 0} data-step-back="" onClick={() => move(step - 1)}>
                Back
              </button>
              <button type="submit" className="mds-button mds-button--primary" data-step-submit="">
                {step === stepNames.length - 1 ? 'Submit application' : 'Continue'}
              </button>
            </div>
          </form>
          <p hidden={!done} data-done="">
            <a className="mds-button mds-button--secondary" href={link('agency-home')}>
              Return home
            </a>
          </p>
        </div>
      </div>
    </TemplatePage>
  )
}
